import React, { useState, useEffect, useContext } from "react";
import DataTableComponent from "react-data-table-component";
import { useHistory, useLocation } from "react-router-dom";
import Swal from "sweetalert2";

import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";

import ViewListIcon from "@mui/icons-material/ViewModule";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import WalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import Templete from "../Templete/Templete";
import AuthService from "../../../services/Admin/AuthService";
import UserService from "../../../services/Admin/UserService";
import LoadingContext from "../../../store/loading-context";
import UserDetailsDialog from "./UserDetails/UserDetailsDialog";
import { states } from "../../../utill/Common";
import HttpError from "../../../utill/HttpError";

const UserList = () => {
  const loadingCtx = useContext(LoadingContext);
  const history = useHistory();
  const location = useLocation();

  const [sessionUserType, setSessionUserType] = useState();

  const [kycStatus, setKycStatus] = useState("");
  const [status, setStatus] = useState("");
  const [userType, setUserType] = useState("");
  const [state, setState] = useState("");
  const [id, setId] = useState("");
  const [userId, setUserId] = useState("");
  const [parentUserId, setParentUserId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedData, setSelectedData] = useState(null);

  const [openUserDetailsDialog, setOpenUserDetailsDialog] = useState(false);
  const [userData, setUserData] = useState(null);

  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [size, setSize] = useState(10);
  const menuOpen = Boolean(anchorEl);

  // Fetch kyc_status from query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const kyc = params.get("kyc_status");
    if (kyc) setKycStatus(kyc);
  }, [location.search]);

  // Fetch users when filters or page change
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycStatus, status, userType, state, id, userId, searchText, page]);

  const fetchUsers = async () => {
    const employeeType = AuthService.getUserType();
    setSessionUserType(employeeType);

    setLoading(true);
    try {
      const token = AuthService.getAccessToken();
      const response = await UserService.fetchAll(
        token,
        kycStatus,
        status,
        userType,
        state,
        parentUserId,
        id,
        userId,
        searchText,
        size,
        page,
      );

      if (response.status !== "SUCCESS") throw new Error(response.message);

      setData(response.data.result);
      setCount(response.data.count);
    } catch (error) {
      console.error(error);
      Swal.fire("Oops!", HttpError.getError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const changeStatusHandler = async (userId) => {
    loadingCtx.startLoading();
    try {
      const token = AuthService.getAccessToken();
      const response = await UserService.changeStatus(token, userId);
      Swal.fire(
        response.status === "SUCCESS" ? "Success!" : "Oops!",
        response.message,
        response.status === "SUCCESS" ? "success" : "error",
      );
      fetchUsers();
    } catch (error) {
      Swal.fire("Oops!", HttpError.getError(error), "error");
    } finally {
      loadingCtx.stopLoading();
    }
  };

  const deleteUserHandler = async (userId) => {
    const confirmed = await Swal.fire({
      title: "Are you sure you want to delete account?",
      text: `User ID: ${userId}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "red",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirmed.isConfirmed) return;

    loadingCtx.startLoading();
    try {
      const token = AuthService.getAccessToken();
      const response = await UserService.deleteUser(token, userId);
      Swal.fire(
        response.status === "SUCCESS" ? "Success!" : "Oops!",
        response.message,
        response.status === "SUCCESS" ? "success" : "error",
      );
      fetchUsers();
    } catch (error) {
      Swal.fire("Oops!", HttpError.getError(error), "error");
    } finally {
      loadingCtx.stopLoading();
    }
  };

  const xlsUserListDownload = async () => {
    loadingCtx.startLoading();
    try {
      const token = AuthService.getAccessToken();
      const response = await UserService.xlsUserList(token, fromDate, toDate);

      if (response.status === "SUCCESS") {
        window.open(response.data.link, "_blank", "noopener,noreferrer");
        setOpenExportDialog(false);
      } else {
        Swal.fire("Oops!", response.message, "error");
      }
    } catch (error) {
      Swal.fire("Oops!", HttpError.getError(error), "error");
    } finally {
      loadingCtx.stopLoading();
    }
  };

  const columns = [
    { name: "User Type", selector: (row) => row.user_type },
    { name: "User ID", selector: (row) => row.user_id },
    { name: "Name", selector: (row) => row.name },
    { name: "Email", selector: (row) => row.email },
    { name: "Mobile", selector: (row) => row.mobile },
    {
      name: "Balance",
      cell: (row) => (
        <span style={{ color: "green", fontWeight: "bold" }}>
          ₹
          {row.tbl_wallet_balances?.[0]?.main_wallet ||
            row.tbl_wallet_balance?.main_wallet ||
            "0"}
        </span>
      ),
    },
    {
      name: "Premium Status",
      cell: (row) => {
        const hasPremium = row?.tbl_subscriptions?.some(
          (sub) => sub?.package_name.toUpperCase() === "PREMIUM",
        );

        return (
          <span
            className={`badge badge-pill ${
              hasPremium ? "bg-success" : "bg-dark"
            }`}
          >
            {hasPremium ? "SUBSCRIBED" : "FALSE"}
          </span>
        );
      },
    },
    {
      name: "KYC Status",
      cell: (row) => {
        let badgeColor;

        switch (row.kyc_status) {
          case "Verified":
            badgeColor = "success"; // green
            break;
          case "Pending":
            badgeColor = "warning"; // yellow
            break;
          case "Rejected":
            badgeColor = "danger"; // red
            break;
          case "Not Applied":
          case "Suspended":
          default:
            badgeColor = "dark"; // black
            break;
        }

        return (
          <span
            className={`badge badge-pill bg-${badgeColor}`}
            style={{ cursor: "pointer" }}
          >
            {row.kyc_status}
          </span>
        );
      },
    },
    {
      name: "Status",
      cell: (row) => (
        <span
          className={`badge badge-pill badge-${
            row.status === "Active"
              ? "success"
              : row.status === "Inactive"
                ? "danger"
                : "warning"
          }`}
          style={{ cursor: "pointer" }}
          onClick={() => changeStatusHandler(row.id)}
        >
          {row.status}
        </span>
      ),
    },
    {
      name: "Parent",
      cell: (row) => {
        const distributor = row.distributor;
        const masterDistributor = row.master;

        // Prefer Distributor first, fallback to MasterDistributor
        const parent = distributor?.name
          ? `${distributor.name} (${distributor.user_id})`
          : masterDistributor?.name
            ? `${masterDistributor.name} (${masterDistributor.user_id})`
            : "—";

        return <span>{parent}</span>;
      },
    },

    { name: "Reg Date", selector: (row) => row.created_on },
    {
      name: "Actions",
      cell: (row) => (
        <div>
          <IconButton
            color="primary"
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
              setSelectedData(row);
            }}
          >
            <MenuOpenIcon />
          </IconButton>

          <IconButton
            color="primary"
            onClick={() => {
              setUserData(row);
              setOpenUserDetailsDialog(true);
            }}
          >
            <ViewListIcon />
          </IconButton>

          <IconButton
            color="primary"
            onClick={() =>
              window.open("/#/admin/user_profile/" + row.user_id, "_blank")
            }
          >
            <EditIcon />
          </IconButton>

          {sessionUserType === "Admin" && (
            <span>
              <IconButton
                color="primary"
                onClick={() =>
                  history.push("/admin/wallet_topup/" + row.user_id)
                }
              >
                <WalletIcon color="secondary" />
              </IconButton>

              <IconButton
                color="primary"
                onClick={() => deleteUserHandler(row.id)}
              >
                <DeleteIcon color="error" />
              </IconButton>
            </span>
          )}
        </div>
      ),
    },
  ];

  return (
    <Templete t1="Home" t2="User List">
      <section className="content">
        <div className="container-fluid">
          <div className="card card-primary">
            <div className="card-header">
              <h3 className="card-title">User List</h3>
            </div>
            <div className="card-body">
              {/* Filters */}
              <div className="row mb-2">
                <div className="col-md-12">
                  <div className="row">
                    {" "}
                    {/* g-3 adds gutter spacing between columns */}
                    {/* KYC Status */}
                    <div className="col-sm-3">
                      <select
                        className="form-control"
                        value={kycStatus}
                        onChange={(e) => setKycStatus(e.target.value)}
                      >
                        <option value="">Select KYC Status</option>
                        <option value="Verified">Verified</option>
                        <option value="Pending">Pending</option>
                        <option value="Rejected">Rejected</option>
                        <option value="Not Applied">Not Applied</option>
                        <option value="Suspended">Suspended</option>
                      </select>
                    </div>
                    {/* Status */}
                    <div className="col-sm-3">
                      <select
                        className="form-control"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="">Select Status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                    {/* User Type */}
                    <div className="col-sm-3">
                      <select
                        className="form-control"
                        value={userType}
                        onChange={(e) => setUserType(e.target.value)}
                      >
                        <option value="">Select User Type</option>
                        <option value="Retailer">Retailer</option>
                        <option value="Distributor">Distributor</option>
                        <option value="Master Distributor">
                          Master Distributor
                        </option>
                      </select>
                    </div>
                    {/* State */}
                    {/* <div className="col-sm-3">
                      <select
                        className="form-control"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                      >
                        <option value="">Select State</option>
                        {states.map((s, i) => (
                          <option key={i} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div> */}
                    {/* Other Filters */}
                    <div className="col-sm-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Parent User ID"
                        value={parentUserId}
                        onChange={(e) => setParentUserId(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="row pt-3">
                    <div className="col-sm-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="ID"
                        value={id}
                        onChange={(e) => setId(e.target.value)}
                      />
                    </div>
                    <div className="col-sm-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="User ID"
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                      />
                    </div>

                    <div className="col-sm-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search Text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                    </div>
                    {/* Buttons */}
                    {sessionUserType === "Admin" && (
                      <div className="col-sm-3 d-flex align-items-center">
                        <button
                          type="button"
                          className="btn btn-primary mr-2"
                          onClick={fetchUsers}
                        >
                          <i className="fa fa-search"></i> Search
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => setOpenExportDialog(true)}
                        >
                          <i className="fa fa-download"></i> Export
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* User Table */}
              <DataTableComponent
                noHeader
                columns={columns}
                data={data}
                progressPending={loading}
                highlightOnHover
                pagination
                paginationServer
                paginationTotalRows={count}
                paginationPerPage={size}
                onChangePage={(page) => {
                  setPage(page);
                  fetchUsers();
                }}
                paginationRowsPerPageOptions={[10, 20, 50, 100, 200, 500, 1000]}
                onChangeRowsPerPage={(size) => {
                  setSize(size);
                  fetchUsers();
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <UserDetailsDialog
        openUserDetailsDialog={openUserDetailsDialog}
        setOpenUserDetailsDialog={setOpenUserDetailsDialog}
        userData={userData}
      />

      <Dialog
        open={openExportDialog}
        onClose={() => setOpenExportDialog(false)}
      >
        <DialogTitle>Export User List</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="From Date"
            type="date"
            fullWidth
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label="To Date"
            type="date"
            fullWidth
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExportDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={xlsUserListDownload}
            color="primary"
            variant="contained"
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Templete>
  );
};

export default UserList;
